import { useState, useEffect, useCallback, useRef } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { tierChangeApi, type TierChangePreview } from '../../api/tierChangeApi';
import { useCurrentOrganization } from '../../hooks/useCurrentOrganization';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { getTierById, formatPrice } from '../../constants/tiers';
import { useActiveDiscount, applyDiscount } from '../../hooks/useActiveDiscount';
import { useQueryClient } from '@tanstack/react-query';

interface TierChangeConfirmScreenProps {
  newTierId: string; // Tier ID to change to
  autoRenewal?: boolean; // Auto-renewal flag (default: true)
  isRenewal?: boolean; // True when renewing same tier
}

type PaymentState = 'idle' | 'creating' | 'redirected' | 'polling' | 'succeeded' | 'failed';

export function TierChangeConfirmScreen({ newTierId, autoRenewal = true, isRenewal = false }: TierChangeConfirmScreenProps) {
  const { popScreen } = useTabNavigationStore();
  const { data: organization } = useCurrentOrganization();
  const queryClient = useQueryClient();
  const { discountPercent, hasActiveDiscount } = useActiveDiscount();

  const [preview, setPreview] = useState<TierChangePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentState, setPaymentState] = useState<PaymentState>('idle');
  const [renewalEnabled, setRenewalEnabled] = useState(autoRenewal);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const newTier = getTierById(newTierId);

  useEffect(() => {
    if (!organization?.id) return;

    const fetchPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await tierChangeApi.preview(organization.id, newTierId);
        setPreview(data);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Не удалось рассчитать preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [organization?.id, newTierId]);

  // Check for returning from payment page
  useEffect(() => {
    const storedPaymentId = sessionStorage.getItem('pending_tier_payment_id');
    if (storedPaymentId) {
      setPendingPaymentId(storedPaymentId);
      setPaymentState('polling');
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, []);

  const startPolling = useCallback((paymentId: string) => {
    setPaymentState('polling');
    let pollCount = 0;

    // Poll every 3 seconds
    pollingRef.current = setInterval(async () => {
      try {
        pollCount++;
        // Every 3rd poll — check directly with payment provider (fallback for missed webhooks)
        const status = pollCount % 3 === 0
          ? await tierChangeApi.checkPaymentWithProvider(paymentId)
          : await tierChangeApi.getPaymentStatus(paymentId);

        if (status.status === 'succeeded') {
          // Success!
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
          sessionStorage.removeItem('pending_tier_payment_id');
          setPaymentState('succeeded');

          // Invalidate queries
          await queryClient.invalidateQueries({ queryKey: ['organization'] });
          await queryClient.invalidateQueries({ queryKey: ['currentOrganization'] });

          try {
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
          } catch {}

          // Auto-navigate after 2 seconds
          setTimeout(() => {
            popScreen(); // Go back to plans/manage screen
            if (!isRenewal) {
              popScreen(); // Go back to subscription manage screen (from plans)
            }
          }, 2000);
        } else if (status.status === 'failed' || status.status === 'canceled') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
          sessionStorage.removeItem('pending_tier_payment_id');
          setPaymentState('failed');
          setError(status.status === 'canceled' ? 'Платёж отменён' : 'Ошибка оплаты');

          try {
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
          } catch {}
        }
        // else: still pending — keep polling
      } catch (err) {
        // Network error — keep polling, don't fail
        console.warn('Payment status poll error:', err);
      }
    }, 3000);

    // Timeout after 90 seconds
    pollingTimeoutRef.current = setTimeout(() => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      setPaymentState('idle');
      sessionStorage.removeItem('pending_tier_payment_id');
      setError('Платёж обрабатывается. Если средства списаны, тариф будет активирован автоматически.');
    }, 90000);
  }, [queryClient, popScreen]);

  // Start polling when we have a pending payment
  useEffect(() => {
    if (paymentState === 'polling' && pendingPaymentId) {
      startPolling(pendingPaymentId);
    }
  }, [paymentState, pendingPaymentId, startPolling]);

  const handleConfirm = async () => {
    if (!organization?.id || !preview) return;

    try {
      setConfirming(true);
      setError(null);

      if (preview.charge_amount > 0) {
        // Paid tier change — create payment and redirect to T-Bank
        setPaymentState('creating');

        const paymentResponse = await tierChangeApi.createPayment({
          organization_id: organization.id,
          new_tier: newTierId,
          auto_renewal: renewalEnabled,
          return_url: '', // use server-side configured URLs
        });

        // Store payment ID for polling on return
        sessionStorage.setItem('pending_tier_payment_id', paymentResponse.payment_id);
        setPendingPaymentId(paymentResponse.payment_id);
        setPaymentState('redirected');

        // Open payment URL
        if (window.Telegram?.WebApp?.openLink) {
          window.Telegram.WebApp.openLink(paymentResponse.confirmation_url);
        } else {
          window.open(paymentResponse.confirmation_url, '_blank');
        }

        // Start polling immediately (user may return quickly)
        setTimeout(() => {
          setPaymentState('polling');
        }, 3000);
      } else {
        // Free tier change (trial) — direct confirm
        await tierChangeApi.confirm(organization.id, preview);

        await queryClient.invalidateQueries({ queryKey: ['organization', organization.id] });
        await queryClient.invalidateQueries({ queryKey: ['currentOrganization'] });

        try {
          window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } catch {}

        alert(isRenewal ? `Подписка ${newTier?.name} продлена!` : `Тариф успешно изменён на ${newTier?.name}!`);
        popScreen();
        if (!isRenewal) {
          popScreen();
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Не удалось создать платёж');
      setPaymentState('idle');
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      } catch {}
    } finally {
      setConfirming(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Payment success screen
  if (paymentState === 'succeeded') {
    return (
      <div className="p-4 space-y-4">
        <PageHeader title="Оплата прошла успешно" />
        <Card>
          <div className="text-center py-8">
            <div className="text-4xl mb-4">✅</div>
            <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {isRenewal ? `Подписка ${newTier?.name} продлена!` : `Тариф ${newTier?.name} активирован!`}
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-hint)' }}>
              Перенаправление...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Payment polling screen
  if (paymentState === 'polling' || paymentState === 'redirected') {
    return (
      <div className="p-4 space-y-4">
        <PageHeader title="Обработка платежа" />
        <Card>
          <div className="text-center py-8">
            <div className="text-4xl mb-4 animate-pulse">⏳</div>
            <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {paymentState === 'redirected' ? 'Ожидание оплаты...' : 'Проверяем статус платежа...'}
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-hint)' }}>
              Не закрывайте это окно
            </p>
          </div>
        </Card>
        <Button onClick={() => {
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
          sessionStorage.removeItem('pending_tier_payment_id');
          setPaymentState('idle');
          setPendingPaymentId(null);
        }} variant="secondary" fullWidth>
          Отменить ожидание
        </Button>
      </div>
    );
  }

  // Payment failed screen
  if (paymentState === 'failed') {
    return (
      <div className="p-4 space-y-4">
        <PageHeader title="Ошибка оплаты" />
        <Card>
          <div className="text-center py-8">
            <div className="text-4xl mb-4">❌</div>
            <p className="text-lg font-semibold" style={{ color: 'var(--danger)' }}>
              {error || 'Платёж не прошёл'}
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-hint)' }}>
              Попробуйте ещё раз или выберите другой способ оплаты
            </p>
          </div>
        </Card>
        <div className="flex gap-3">
          <Button onClick={() => popScreen()} variant="secondary" fullWidth>
            Назад
          </Button>
          <Button onClick={() => {
            setPaymentState('idle');
            setError(null);
          }} fullWidth>
            Попробовать снова
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Расчёт стоимости...</p>
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="p-4 space-y-4">
        <PageHeader title="Ошибка" />
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
        <Button onClick={() => popScreen()} variant="secondary" fullWidth>
          Назад
        </Button>
      </div>
    );
  }

  if (!preview || !newTier) {
    return null;
  }

  const isFreeTrial = preview.trial_days_granted > 0;
  const isDowngrade = !preview.is_upgrade && preview.current_tier !== 'free';
  const isPaid = preview.charge_amount > 0;

  // Применяем активный промокод к отображаемой сумме (бэкенд применит его же при создании платежа)
  const discountedChargeAmount = hasActiveDiscount && isPaid
    ? applyDiscount(preview.charge_amount, discountPercent)
    : preview.charge_amount;
  const discountedNewPrice = hasActiveDiscount && preview.new_price > 0
    ? applyDiscount(preview.new_price, discountPercent)
    : preview.new_price;

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title={isRenewal ? 'Продление подписки' : 'Подтверждение смены тарифа'}
        subtitle={isRenewal ? 'Проверьте детали продления' : 'Проверьте детали перед подтверждением'}
      />

      {error && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {/* New Tier Info */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          {isRenewal ? 'Тариф' : 'Новый тариф'}
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
            {newTier.name}
          </span>
          {hasActiveDiscount && preview.new_price > 0 ? (
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-semibold" style={{ color: 'var(--accent)' }}>
                {formatPrice(discountedNewPrice)}
              </span>
              <span className="text-sm line-through" style={{ color: 'var(--text-hint)' }}>
                {formatPrice(preview.new_price)}
              </span>
            </div>
          ) : (
            <span className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              {formatPrice(preview.new_price)}
            </span>
          )}
        </div>
      </Card>

      {/* Trial Notice */}
      {isFreeTrial && (
        <Card>
          <div className="text-center py-2">
            <p className="text-sm font-semibold" style={{ color: 'var(--accent)' }}>
              🎉 Вы получаете {preview.trial_days_granted} дней бесплатного триала!
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
              Полный доступ ко всем функциям тарифа {newTier.name}
            </p>
          </div>
        </Card>
      )}

      {/* Calculation Details (for paid tier changes) */}
      {!isFreeTrial && preview.current_tier !== 'free' && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Детали расчёта
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Текущий тариф:</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                {preview.current_tier.toUpperCase()} ({formatPrice(preview.current_price)})
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Осталось дней:</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                {preview.days_remaining} дней
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Остаток средств:</span>
              <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                {preview.remaining_value.toFixed(2)} ₽
              </span>
            </div>
            <div className="h-px bg-[var(--separator)] my-2" />
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Новая длительность:</span>
              <span style={{ color: 'var(--accent)' }} className="font-semibold">
                {preview.new_duration} дней
              </span>
            </div>
            {isDowngrade && (
              <p className="text-xs mt-2" style={{ color: 'var(--text-hint)' }}>
                При переходе на более дешёвый тариф остаток конвертируется в дополнительные дни
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Summary */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Итого
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span style={{ color: 'var(--text-hint)' }}>К оплате:</span>
            {preview.charge_amount === 0 ? (
              <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
                Бесплатно
              </span>
            ) : hasActiveDiscount ? (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
                  {discountedChargeAmount} ₽
                </span>
                <span className="text-sm line-through" style={{ color: 'var(--text-hint)' }}>
                  {preview.charge_amount} ₽
                </span>
                <span
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--accent)', color: '#fff' }}
                >
                  −{discountPercent}%
                </span>
              </div>
            ) : (
              <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
                {preview.charge_amount} ₽
              </span>
            )}
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-hint)' }}>Активен до:</span>
            <span style={{ color: 'var(--text-primary)' }} className="font-medium">
              {formatDate(preview.new_expires_at)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--separator)' }}>
            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
              Автопродление
            </span>
            <button
              onClick={() => setRenewalEnabled(!renewalEnabled)}
              className="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors"
              style={{ backgroundColor: renewalEnabled ? 'var(--accent)' : 'var(--bg-secondary)' }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: renewalEnabled ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={() => popScreen()} variant="secondary" fullWidth disabled={confirming}>
          Отмена
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={confirming || paymentState === 'creating'}
          fullWidth
        >
          {confirming || paymentState === 'creating'
            ? 'Создание платежа...'
            : isPaid
              ? `Оплатить ${discountedChargeAmount} ₽`
              : 'Активировать триал'}
        </Button>
      </div>
    </div>
  );
}
